/**
 * Sky — the WebGL2 / GLSL sibling (AD-651 item 2 ⇒ AD-9004; grade AD-9006; clouds + the
 * Cloud's fringe AD-9007, Art Direction Bible §8.5).
 *
 * A plan's `SkyPlan` becomes ONE mesh, `sky`: three r185's own Preetham sky,
 * `examples/jsm/objects/Sky.js` (a `ShaderMaterial` on a unit box, BackSide, depth pinned to
 * the far plane), with its stock disc and stock clouds OFF and its output routed through
 * `skyCompose()`:
 *
 *   exposure → colour grade (saturation, tint) → the cloud layer (+ The Great Cloud-iridescence
 *   fringe on thin edges near the sun) → the soft luminance cap (under the bloom
 *   threshold) → + the sun disc, AFTER the cap and hidden by cloud, so only the disc blooms.
 *
 * Every step has a CPU twin in skyModel.ts (`skyColourGrade`, `cloudSample`, `skyCap`) and a
 * TSL twin in skyNodeMesh.ts (the WebGPU sibling, chosen through the `CreatedRenderer`
 * factory bag exactly like the ground, AD-652). The cloud layer drifts on `scTime`, set per
 * draw from the page clock — or held at 0 by the `skystill` dev flag (determinism contract).
 *
 * Presentation-only; off the per-tick path. Import boundary (AD-122): frontend-local.
 */

import { Vector2, Vector3, type Mesh } from 'three';
import { Sky } from 'three/examples/jsm/objects/Sky.js';

import type { SkyPlan } from './plan.js';
import { CLOUD_IRIDESCENCE, type CloudParams } from './skyModel.js';

/** Per-renderer build options the builder decides (not plan data). */
export interface SkyOptions {
  /** Mobile tier / `?quality=low`: fewer cloud octaves. */
  readonly lite?: boolean;
  /** Hold the cloud drift at t = 0 (`skystill` dev flag — the lane / captures). */
  readonly still?: boolean;
}

/** A renderer's sky constructor — `createSky` (WebGL2) or `createSkyNodeMesh` (WebGPU). */
export type SkyFactory = (plan: SkyPlan, options?: SkyOptions) => Mesh;

/** Cloud fbm octaves for a plan + options: the plan's count, one fewer (min 2) when lite. */
export function cloudOctaves(clouds: CloudParams, options: SkyOptions = {}): number {
  return options.lite ? Math.max(2, clouds.octaves - 1) : clouds.octaves;
}

/** The sun disc's soft edge, as a fraction of its angular radius (anti-aliasing). */
export const SUN_DISC_EDGE = 0.2;

/** cos(outer), cos(inner) of the disc's angular radius — the smoothstep bounds. */
export function sunDiscCosines(sunDiscDegrees: number): [number, number] {
  const r = ((sunDiscDegrees / 2) * Math.PI) / 180;
  return [Math.cos(r), Math.cos(r * (1 - SUN_DISC_EDGE))];
}

/**
 * The parameter values a built sky carries, mirrored as plain data on
 * `mesh.userData.skyValues` by BOTH siblings so a test (or a dev page) reads either alike.
 */
export interface SkyValues {
  turbidity: number;
  rayleigh: number;
  mieCoefficient: number;
  mieDirectionalG: number;
  exposure: number;
  saturation: number;
  tint: [number, number, number];
  knee: number;
  ceiling: number;
  /** The stock disc / stock clouds: always 0 — ours replace them. */
  showSunDisc: number;
  cloudCoverage: number;
  sunPosition: [number, number, number];
  sunDiscCos: [number, number];
  sunDiscColour: [number, number, number];
  clouds: {
    coverage: number;
    softness: number;
    scale: number;
    opacity: number;
    litColour: [number, number, number];
    shadeColour: [number, number, number];
    rimColour: [number, number, number];
    iridescence: number;
    drift: [number, number];
    octaves: number;
  } | null;
  still: boolean;
  /** Half-extent of the dome box, metres. */
  radius: number;
}

/** The mirror both siblings write — one function, so they cannot drift apart. */
export function skyValues(plan: SkyPlan, options: SkyOptions = {}): SkyValues {
  const c = plan.clouds;
  const v3 = (t: readonly [number, number, number]): [number, number, number] => [t[0], t[1], t[2]];
  return {
    turbidity: plan.turbidity,
    rayleigh: plan.rayleigh,
    mieCoefficient: plan.mieCoefficient,
    mieDirectionalG: plan.mieDirectionalG,
    exposure: plan.exposure,
    saturation: plan.saturation,
    tint: v3(plan.tint),
    knee: plan.knee,
    ceiling: plan.ceiling,
    showSunDisc: 0,
    cloudCoverage: 0,
    sunPosition: [plan.sun.x, plan.sun.y, plan.sun.z],
    sunDiscCos: sunDiscCosines(plan.sunDiscDegrees),
    sunDiscColour: [
      plan.sunDiscTint[0] * plan.sunDiscLuminance,
      plan.sunDiscTint[1] * plan.sunDiscLuminance,
      plan.sunDiscTint[2] * plan.sunDiscLuminance,
    ],
    clouds: c
      ? {
          coverage: c.coverage,
          softness: c.softness,
          scale: c.scale,
          opacity: c.opacity,
          litColour: v3(c.litColour),
          shadeColour: v3(c.shadeColour),
          rimColour: v3(c.rimColour),
          iridescence: c.iridescence,
          drift: [c.drift[0], c.drift[1]],
          octaves: cloudOctaves(c, options),
        }
      : null,
    still: options.still === true,
    radius: plan.radius,
  };
}

/** The stock fragment's final write, and the composed replacement. */
export const SKY_FRAGMENT_OUTPUT = 'gl_FragColor = vec4( texColor, 1.0 );';
const SKY_FRAGMENT_OUTPUT_COMPOSED =
  'gl_FragColor = vec4( skyCompose( texColor * skyExposure, direction, vSunDirection ), 1.0 );';

const glslVec3 = (t: readonly number[]): string =>
  `vec3( ${t.map((v) => v.toFixed(4)).join(', ')} )`;

/**
 * The GLSL prefix: uniforms + the grade, cap, cloud layer and composition — statement for
 * statement the CPU twins in skyModel.ts. `octaves` is baked in as a define (a constant loop
 * bound, like the ground's), so the mobile/low tier compiles a cheaper loop.
 */
export function skyPrefixGlsl(octaves: number): string {
  const [c, m, g] = CLOUD_IRIDESCENCE;
  return `#define SC_OCTAVES ${octaves}
uniform float skyExposure;
uniform float skySaturation;
uniform vec3 skyTint;
uniform float skyKnee;
uniform float skyCeiling;
uniform float scCoverage;
uniform float scSoftness;
uniform float scScale;
uniform float scOpacity;
uniform vec3 scLit;
uniform vec3 scShade;
uniform vec3 scRim;
uniform float scIridescence;
uniform vec2 scDrift;
uniform float scTime;
uniform vec2 sdCos;
uniform vec3 sdColour;
const vec3 skyLumaW = vec3( 0.2126, 0.7152, 0.0722 );
vec3 skyColourGrade( vec3 c ) {
	float l = dot( c, skyLumaW );
	return max( vec3( l ) + ( c - vec3( l ) ) * skySaturation, vec3( 0.0 ) ) * skyTint;
}
vec3 skyCap( vec3 c ) {
	float l = dot( c, skyLumaW );
	if ( l > skyKnee ) {
		float span = skyCeiling - skyKnee;
		c *= ( skyKnee + span * ( 1.0 - exp( - ( l - skyKnee ) / span ) ) ) / l;
	}
	return c;
}
highp float scHash( highp vec2 p ) {
	highp vec3 p3 = fract( vec3( p.xyx ) * 0.1031 );
	p3 += dot( p3, p3.yzx + 33.33 );
	return fract( ( p3.x + p3.y ) * p3.z );
}
highp float scNoise( highp vec2 p ) {
	highp vec2 i = floor( p );
	highp vec2 f = fract( p );
	highp vec2 u = f * f * ( 3.0 - 2.0 * f );
	return mix( mix( scHash( i ), scHash( i + vec2( 1.0, 0.0 ) ), u.x ),
		mix( scHash( i + vec2( 0.0, 1.0 ) ), scHash( i + vec2( 1.0, 1.0 ) ), u.x ), u.y );
}
highp float scFbm( highp vec2 p ) {
	highp float sum = 0.0;
	highp float amp = 0.5;
	highp float norm = 0.0;
	for ( int o = 0; o < SC_OCTAVES; o ++ ) {
		sum += amp * scNoise( p );
		norm += amp;
		p = mat2( 1.6, 1.2, - 1.2, 1.6 ) * p + 17.0;
		amp *= 0.5;
	}
	return sum / norm;
}
vec3 scIrid( float t ) {
	float ph = fract( t ) * 3.0;
	vec3 v = mix( ${glslVec3(c)}, ${glslVec3(m)}, clamp( ph, 0.0, 1.0 ) );
	v = mix( v, ${glslVec3(g)}, clamp( ph - 1.0, 0.0, 1.0 ) );
	return mix( v, ${glslVec3(c)}, clamp( ph - 2.0, 0.0, 1.0 ) );
}
vec4 skyClouds( vec3 d, vec3 s ) {
	float h = max( d.y, 0.0 );
	float fade = smoothstep( 0.02, 0.18, h );
	if ( fade <= 0.0 || scCoverage <= 0.0 ) return vec4( 0.0 );
	highp vec2 p = d.xz * ( scScale / ( h + 0.1 ) ) + scDrift * scTime;
	float n = scFbm( p );
	float lo = 1.0 - scCoverage;
	float cover = smoothstep( lo - scSoftness, lo + scSoftness, n );
	if ( cover <= 0.0 ) return vec4( 0.0 );
	vec2 sxz = s.xz / max( length( s.xz ), 1e-4 );
	float n2 = scFbm( p + sxz * 0.6 );
	float lit = smoothstep( 0.35, 0.65, clamp( 0.5 + ( n - n2 ) * 4.0, 0.0, 1.0 ) );
	float sunDot = max( dot( d, s ), 0.0 );
	float edge = cover * ( 1.0 - cover ) * 4.0;
	float rim = pow( sunDot, 6.0 ) * edge;
	float irid = scIridescence * edge * smoothstep( 0.6, 0.97, sunDot );
	vec3 spec = scIrid( n * 2.5 + sunDot * 1.5 );
	vec3 base = mix( scShade, scLit, lit ) + scRim * rim;
	return vec4( mix( base, base * ( 0.45 + spec * 1.1 ), irid ), cover * fade * scOpacity );
}
vec3 skyCompose( vec3 sky, vec3 d, vec3 s ) {
	vec4 cl = skyClouds( d, s );
	vec3 c = skyCap( mix( skyColourGrade( sky ), cl.rgb, cl.a ) );
	float disc = smoothstep( sdCos.x, sdCos.y, dot( d, s ) ) * ( 1.0 - cl.a );
	return c + sdColour * disc;
}
`;
}

/**
 * Route three's sky fragment through `skyCompose()`. Throws if the stock output line is not
 * found (three changed the shader) — a loud failure in the sibling test, never a silently
 * ungraded, cloudless sky.
 */
export function patchSkyFragment(fragmentShader: string, octaves = 4): string {
  if (!fragmentShader.includes(SKY_FRAGMENT_OUTPUT)) {
    throw new Error('sky: three Sky.js fragment output line not found — re-check the patch');
  }
  return `${skyPrefixGlsl(octaves)}${fragmentShader.replace(
    SKY_FRAGMENT_OUTPUT,
    SKY_FRAGMENT_OUTPUT_COMPOSED,
  )}`;
}

/** Seconds on the cloud clock: the page clock, or 0 when held still. */
export function skyClock(still: boolean): number {
  return still ? 0 : performance.now() / 1000;
}

/** Build the WebGL2 sky for a plan (the default `SceneBuilder` sky). */
export function createSky(plan: SkyPlan, options: SkyOptions = {}): Mesh {
  const sky = new Sky();
  const values = skyValues(plan, options);
  const material = sky.material;
  material.fragmentShader = patchSkyFragment(material.fragmentShader, values.clouds?.octaves ?? 4);
  const u = material.uniforms as Record<string, { value: unknown }>;
  u.turbidity!.value = values.turbidity;
  u.rayleigh!.value = values.rayleigh;
  u.mieCoefficient!.value = values.mieCoefficient;
  u.mieDirectionalG!.value = values.mieDirectionalG;
  u.showSunDisc!.value = values.showSunDisc;
  u.cloudCoverage!.value = values.cloudCoverage;
  u.time!.value = 0;
  (u.sunPosition!.value as Vector3).set(...values.sunPosition);
  u.skyExposure = { value: values.exposure };
  u.skySaturation = { value: values.saturation };
  u.skyTint = { value: new Vector3(...values.tint) };
  u.skyKnee = { value: values.knee };
  u.skyCeiling = { value: values.ceiling };
  const cl = values.clouds;
  u.scCoverage = { value: cl?.coverage ?? 0 };
  u.scSoftness = { value: cl?.softness ?? 0.1 };
  u.scScale = { value: cl?.scale ?? 1 };
  u.scOpacity = { value: cl?.opacity ?? 0 };
  u.scLit = { value: new Vector3(...(cl?.litColour ?? [1, 1, 1])) };
  u.scShade = { value: new Vector3(...(cl?.shadeColour ?? [0.5, 0.5, 0.5])) };
  u.scRim = { value: new Vector3(...(cl?.rimColour ?? [0, 0, 0])) };
  u.scIridescence = { value: cl?.iridescence ?? 0 };
  u.scDrift = { value: new Vector2(...(cl?.drift ?? [0, 0])) };
  const time = { value: skyClock(values.still) };
  u.scTime = time;
  u.sdCos = { value: new Vector2(...values.sunDiscCos) };
  u.sdColour = { value: new Vector3(...values.sunDiscColour) };
  material.name = 'sky:preetham';
  sky.onBeforeRender = () => {
    time.value = skyClock(values.still);
  };
  finishSky(sky, values);
  return sky;
}

/** Shared mesh setup: size, name, no shadows, never culled, drawn after opaques. */
export function finishSky(mesh: Mesh, values: SkyValues): void {
  // three's box is 1 m across: scale so its half-extent is the plan radius.
  mesh.scale.setScalar(values.radius * 2);
  mesh.name = 'sky';
  mesh.castShadow = false;
  mesh.receiveShadow = false;
  // The camera always sits inside the box; skipping the frustum test is exact and free.
  mesh.frustumCulled = false;
  // Drawn AFTER the opaque world (renderOrder sorts first): its depth is the far plane, so
  // every pixel the world already covers fails the depth test early and the cloud fbm runs
  // for visible sky only — on a ground-heavy frame most of the screen.
  mesh.renderOrder = 1;
  mesh.userData.skyValues = values;
}
